import { useQuery } from '@tanstack/react-query';
import { queryKeys, type UnitMember } from '@vtak/shared';
import { workspaceClient } from '@/services/workspace-client';
import { VirtualRowList } from '@/components/common/virtual-row-list';
import { Badge } from '@/components/ui/badge';

/** Approved members of a unit with their roles. */
export function MemberList({ unitId }: { unitId: string }): React.JSX.Element {
  const query = useQuery({
    queryKey: [...queryKeys.workspace.members(unitId), 'approved'],
    queryFn: () => workspaceClient.getMembers(unitId, 'approved'),
  });

  if (query.isLoading) {
    return <p className="p-4 text-center text-xs text-muted-foreground">Loading members…</p>;
  }

  return (
    <VirtualRowList<UnitMember>
      items={query.data ?? []}
      rowHeight={48}
      keyOf={(m) => m.id}
      emptyText="No members yet"
      renderRow={(member) => <MemberRow member={member} />}
    />
  );
}

/** A single approved member row. */
function MemberRow({ member }: { member: UnitMember }): React.JSX.Element {
  return (
    <div className="mx-2 mb-1 flex items-center justify-between gap-2 rounded-md bg-white/[0.04] px-3 py-2">
      <span className="min-w-0 flex-1 truncate font-mono text-xs text-foreground">
        {shortSub(member.keycloakSub)}
      </span>
      <Badge variant={member.role === 'admin' ? 'default' : 'outline'} className="text-[10px]">
        {member.role}
      </Badge>
    </div>
  );
}

/** Shorten a Keycloak subject id for display. */
function shortSub(sub: string): string {
  return sub.length > 20 ? `${sub.slice(0, 20)}…` : sub;
}
